// Refactor 02/2026 — extrahiert aus AppointmentsPage.js.
// Verkapselt das gesamte Drag&Drop-Verhalten der Tagesansicht:
//   - State: draggedAppointment, draggedParticipant, dropTarget, dragCursorPos
//   - Handler: handleDragStart, handleParticipantDragStart, handleDragEnd,
//              handleDragOver, handleDragLeave, handleDrop, handleParticipantDrop
//
// Verhalten ist identisch zum vorherigen Inline-Code:
//   - Termine werden via PUT /api/appointments/:id verschoben
//   - Teilnehmer via POST /api/appointments/:id/move-participant
//   - Slot-Validierung (Mindbody-blockiert / Pausen) via utilGetSlotBlockReason
//   - Customer-Konflikt-Preflight zeigt window.confirm bei Termin am Vortag etc.
//   - Auto-Scroll am viewport-Rand via startEdgeAutoScroll
import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getSlotBlockReason as utilGetSlotBlockReason } from '@/utils/trainerSlotLogic';
import {
  computeAdjustedSlot,
  computeDropTimes,
  computeDropFromGrid,
  isMindbodyOnlyAppointment,
  startEdgeAutoScroll,
  computeOccupancy,
  getMaxParticipants,
} from '@/utils/calendarDragDrop';
import { runCustomerConflictCheck } from '@/utils/customerConflictCheck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAppointmentDragDrop = ({
  appointments,
  mindbodySlotsByTrainer,
  services,
  customers,
  currentDate,
  fetchAppointments,
  // 06/2026 Drop-Präzision: Grid-Geometrie für cursor-basierte Zeitberechnung
  gridRef,
  slotHeight = 60,
  calendarStartHour = 0,
  calendarEndHour = 24,
}) => {
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [draggedParticipant, setDraggedParticipant] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dragCursorPos, setDragCursorPos] = useState(null);
  const dragScrollRef = useRef(null);
  // 10/2026: Pixel-Abstand zwischen Cursor und Oberkante des gegriffenen Blocks
  // beim Drag-Start. Wird abgezogen, damit die OBERKANTE des Termins dort
  // einrastet, wo das Drag-Bild visuell liegt (statt der Cursor-Position).
  const grabOffsetRef = useRef(0);

  // Cursor-Y → Ziel-Zeit relativ zum Grid (unabhängig vom Event-Target).
  const computeFromCursor = useCallback(
    (clientY, apptLike) => {
      const rect = gridRef?.current?.getBoundingClientRect?.();
      const appt = apptLike || draggedAppointment || draggedParticipant?.appointment;
      if (!rect || !appt) return null;
      const durationMinutes =
        (new Date(appt.end_time) - new Date(appt.start_time)) / 60000;
      return computeDropFromGrid({
        clientY: clientY - grabOffsetRef.current,
        gridRect: rect,
        slotHeight,
        calendarStartMinutes: calendarStartHour * 60,
        calendarEndMinutes: calendarEndHour * 60,
        durationMinutes,
        currentDate,
        // Raster ist 15-Min → exakt auf den sichtbaren Slot einrasten (statt 5-Min
        // „dazwischen"). Behebt „muss hin- und herschieben bis es reinpasst".
        snapMinutes: 15,
      });
    },
    [gridRef, draggedAppointment, draggedParticipant, slotHeight, calendarStartHour, calendarEndHour, currentDate]
  );

  const getSlotBlockReason = useCallback(
    (trainerId, newStart, newEnd, excludeAppointmentIds = [], opts = {}) =>
      utilGetSlotBlockReason(
        trainerId,
        newStart,
        newEnd,
        excludeAppointmentIds,
        opts,
        { mindbodySlotsByTrainer, appointments }
      ),
    [mindbodySlotsByTrainer, appointments]
  );

  // 10/2026 Feature "Schnell-Rückgängig": zeigt nach dem Verschieben eine
  // Undo-Aktion im Toast. undoFn macht den serverseitigen Move rückgängig.
  const showUndoToast = useCallback(
    (message, undoFn) => {
      toast.success(message, {
        action: {
          label: 'Rückgängig',
          onClick: async () => {
            try {
              await undoFn();
              fetchAppointments();
              toast.success('Verschiebung rückgängig gemacht');
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Rückgängig fehlgeschlagen');
              fetchAppointments();
            }
          },
        },
        duration: 8000,
      });
    },
    [fetchAppointments]
  );

  // 10/2026 Feature "Konflikt-Vorwarnung": Ermittelt live beim Ziehen, ob der
  // Ziel-Slot belegt/blockiert ist → Vorschau-Rechteck wird rot.
  const computeDropBlocked = useCallback(
    (trainerData, newStart, newEnd) => {
      try {
        if (draggedParticipant) {
          const srcApt = draggedParticipant.appointment;
          const joinable = appointments.some((apt) => {
            if (apt.id === draggedParticipant.appointmentId) return false;
            if (apt.trainer_id !== trainerData.trainer.id) return false;
            if (apt.service_id !== draggedParticipant.serviceId) return false;
            if (Math.abs(new Date(apt.start_time) - newStart) > 60000) return false;
            if (Math.abs(new Date(apt.end_time) - newEnd) > 60000) return false;
            const svc = services.find((s) => s.id === apt.service_id);
            return (apt.customer_ids?.length || 0) < (svc?.max_participants || 1);
          });
          if (joinable) return false;
          const cnt = srcApt.customer_ids?.length || (srcApt.customer_id ? 1 : 0);
          const excludeIds = cnt <= 1 ? [draggedParticipant.appointmentId] : [];
          const svc = services.find((s) => s.id === srcApt.service_id);
          return !!getSlotBlockReason(trainerData.trainer.id, newStart, newEnd, excludeIds, {
            maxParticipants: svc?.max_participants || 1,
            occupancy: 1,
          });
        }
        if (draggedAppointment) {
          return !!getSlotBlockReason(trainerData.trainer.id, newStart, newEnd, [draggedAppointment.id], {
            maxParticipants: getMaxParticipants(services, draggedAppointment.service_id),
            occupancy: computeOccupancy(draggedAppointment),
          });
        }
      } catch (_e) {
        return false;
      }
      return false;
    },
    [draggedParticipant, draggedAppointment, appointments, services, getSlotBlockReason]
  );

  const handleDragStart = (e, appointment) => {
    setDraggedAppointment(appointment);
    const rect = e.currentTarget?.getBoundingClientRect?.();
    grabOffsetRef.current = rect ? e.clientY - rect.top : 0;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    e.target.style.opacity = '0.5';
  };

  const handleParticipantDragStart = (e, appointment, customerId, customerName) => {
    e.stopPropagation();
    const rect = e.currentTarget?.getBoundingClientRect?.();
    grabOffsetRef.current = rect ? e.clientY - rect.top : 0;
    setDraggedParticipant({
      appointmentId: appointment.id,
      appointment,
      customerId,
      customerName,
      serviceId: appointment.service_id,
      serviceName: appointment.service_name,
    });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    grabOffsetRef.current = 0;
    setDraggedAppointment(null);
    setDraggedParticipant(null);
    setDropTarget(null);
    setDragCursorPos(null);
    if (dragScrollRef.current) {
      clearInterval(dragScrollRef.current);
      dragScrollRef.current = null;
    }
  };

  const handleDragOver = (e, trainerData, timeSlot) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragCursorPos({ x: e.clientX, y: e.clientY });

    if (dragScrollRef.current) {
      clearInterval(dragScrollRef.current);
      dragScrollRef.current = null;
    }
    dragScrollRef.current = startEdgeAutoScroll(e.clientY);

    if (draggedAppointment || draggedParticipant) {
      const fromCursor = computeFromCursor(e.clientY);
      if (fromCursor) {
        // Exakte Cursor-Position → präziser Slot. Nur updaten wenn sich der
        // Ziel-Slot wirklich ändert (verhindert Re-Render-Flackern, das Drops
        // „verschluckt" → „wiggle bis es greift").
        const blocked = computeDropBlocked(
          trainerData,
          fromCursor.newStartTime,
          fromCursor.newEndTime
        );
        setDropTarget((prev) => {
          if (
            prev &&
            prev.trainer?.trainer?.id === trainerData.trainer.id &&
            prev.minutes === fromCursor.snappedMinutes &&
            prev.blocked === blocked
          ) {
            return prev;
          }
          return {
            trainer: trainerData,
            timeSlot: fromCursor.label,
            minutes: fromCursor.snappedMinutes,
            blocked,
          };
        });
      } else {
        const adjustedTimeSlot = computeAdjustedSlot(e, timeSlot).adjustedTimeSlot;
        setDropTarget({ trainer: trainerData, timeSlot: adjustedTimeSlot });
      }
    }
  };

  const handleDragLeave = () => {
    // Beim Wechsel zwischen Zellen NICHT löschen (sonst flackert die Vorschau
    // und Drops gehen verloren). Aufräumen erledigt handleDragEnd/handleDrop.
  };

  const handleParticipantDrop = async (e, trainerData, timeSlot) => {
    if (!draggedParticipant) return;

    // 10/2026 Undo: Original-Zustand des Teilnehmers vor dem Move merken.
    const _pCustomerId = draggedParticipant.customerId;
    const _pCustomerName = draggedParticipant.customerName;
    const _pOrigTrainerId = draggedParticipant.appointment.trainer_id;
    const _pOrigStartIso = draggedParticipant.appointment.start_time;
    const _pOrigEndIso = draggedParticipant.appointment.end_time;
    const _pSrcApptId = draggedParticipant.appointmentId;
    const _pSrcCount =
      draggedParticipant.appointment.customer_ids?.length ||
      (draggedParticipant.appointment.customer_id ? 1 : 0);
    const _pOrigFlexSlotId = draggedParticipant.appointment.flex_slot_id ?? null;
    const _pOrigFlexPosition = draggedParticipant.appointment.flex_position ?? null;

    try {
      const _fromCursor = computeFromCursor(e.clientY, draggedParticipant.appointment);
      const { newStartTime, newEndTime } = _fromCursor || computeDropTimes(
        e,
        timeSlot,
        currentDate,
        draggedParticipant.appointment
      );

      // 08/2026 Schutz vor "Sprung"-Drops (analog handleDrop)
      const _pOrigStart = new Date(draggedParticipant.appointment.start_time);
      const _pHourDiff = Math.abs(newStartTime - _pOrigStart) / 3600000;
      if (_pHourDiff >= 3) {
        const _pHhmm = `${String(newStartTime.getHours()).padStart(2, '0')}:${String(newStartTime.getMinutes()).padStart(2, '0')}`;
        const _pOrigHhmm = `${String(_pOrigStart.getHours()).padStart(2, '0')}:${String(_pOrigStart.getMinutes()).padStart(2, '0')}`;
        if (!window.confirm(`Grosser Zeitsprung: Teilnehmer wirklich von ${_pOrigHhmm} auf ${_pHhmm} Uhr verschieben?`)) {
          setDraggedParticipant(null);
          setDropTarget(null);
          return;
        }
      }

      // Check if there's an existing appointment at this time/trainer with same service and available space
      const existingApt = appointments.find((apt) => {
        if (apt.id === draggedParticipant.appointmentId) return false;
        if (apt.trainer_id !== trainerData.trainer.id) return false;
        if (apt.service_id !== draggedParticipant.serviceId) return false;

        const aptStart = new Date(apt.start_time);
        const aptEnd = new Date(apt.end_time);

        if (Math.abs(aptStart - newStartTime) > 60000) return false;
        if (Math.abs(aptEnd - newEndTime) > 60000) return false;

        const service = services.find((s) => s.id === apt.service_id);
        const maxParticipants = service?.max_participants || 1;
        const currentParticipants = apt.customer_ids?.length || 0;

        return currentParticipants < maxParticipants;
      });

      // Slot-Validierung (06/2026): Wenn KEIN passender Gruppen-Termin zum
      // Beitreten existiert, darf der Ziel-Slot nicht belegt/Pause sein.
      // Der Quell-Termin wird nur ausgenommen, wenn er durch den Move leer
      // wird (letzter Teilnehmer → Termin wird gelöscht).
      if (!existingApt) {
        const srcApt = draggedParticipant.appointment;
        const srcParticipantCount =
          srcApt.customer_ids?.length || (srcApt.customer_id ? 1 : 0);
        const excludeIds =
          srcParticipantCount <= 1 ? [draggedParticipant.appointmentId] : [];
        const srcSvc = services.find((s) => s.id === srcApt.service_id);
        const blockReason = getSlotBlockReason(
          trainerData.trainer.id,
          newStartTime,
          newEndTime,
          excludeIds,
          { maxParticipants: srcSvc?.max_participants || 1, occupancy: 1 }
        );
        if (blockReason) {
          toast.error(blockReason);
          setDraggedParticipant(null);
          setDropTarget(null);
          return;
        }
      }

      await axios.post(
        `${API}/appointments/${draggedParticipant.appointmentId}/move-participant`,
        {
          customer_id: draggedParticipant.customerId,
          target_trainer_id: trainerData.trainer.id,
          target_start_time: newStartTime.toISOString(),
          target_end_time: newEndTime.toISOString(),
          target_appointment_id: existingApt?.id || null,
        }
      ).then((_resp) => {
        // 10/2026 Undo: Kunde zurück auf Original-Trainer/-Zeit bewegen.
        // Bei Gruppen-Quelle (noch Teilnehmer übrig) zurück in Quell-Termin,
        // sonst in-place zurück (target_appointment_id: null).
        const _movedId = _resp?.data?.target_appointment_id || _pSrcApptId;
        showUndoToast(`${_pCustomerName} verschoben!`, () =>
          axios.post(`${API}/appointments/${_movedId}/move-participant`, {
            customer_id: _pCustomerId,
            target_trainer_id: _pOrigTrainerId,
            target_start_time: _pOrigStartIso,
            target_end_time: _pOrigEndIso,
            target_appointment_id: _pSrcCount > 1 ? _pSrcApptId : null,
            // FLEX-Gruppierung wiederherstellen (nur relevant beim In-Place-Zurück)
            restore_flex_slot_id: _pSrcCount > 1 ? undefined : _pOrigFlexSlotId,
            restore_flex_position: _pSrcCount > 1 ? undefined : _pOrigFlexPosition,
          })
        );
      });

      setDraggedParticipant(null);
      setDropTarget(null);
      fetchAppointments();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Fehler beim Verschieben des Teilnehmers'
      );
      setDraggedParticipant(null);
      setDropTarget(null);
    }
  };

  const handleDrop = async (e, trainerData, timeSlot) => {
    e.preventDefault();
    if (dragScrollRef.current) {
      clearInterval(dragScrollRef.current);
      dragScrollRef.current = null;
    }

    if (draggedParticipant) {
      await handleParticipantDrop(e, trainerData, timeSlot);
      return;
    }

    if (!draggedAppointment) return;

    if (isMindbodyOnlyAppointment(draggedAppointment)) {
      toast.error(
        'Mindbody-Termine müssen direkt in Mindbody verschoben werden — nicht hier.'
      );
      setDraggedAppointment(null);
      setDropTarget(null);
      return;
    }

    try {
      // 10/2026 Undo: Original-Zustand des Termins vor dem Move merken.
      const _undoId = draggedAppointment.id;
      const _undoData = {
        trainer_id: draggedAppointment.trainer_id,
        start_time: draggedAppointment.start_time,
        end_time: draggedAppointment.end_time,
      };
      const _fromCursor = computeFromCursor(e.clientY, draggedAppointment);
      const { newStartTime, newEndTime } = _fromCursor || computeDropTimes(
        e,
        timeSlot,
        currentDate,
        draggedAppointment
      );

      // 08/2026 Schutz vor "Sprung"-Drops: Landet der Termin ≥3h von der
      // Original-Zeit entfernt (z.B. ungenaues Loslassen + Auto-Scroll →
      // Drop frühmorgens), erst bestätigen lassen.
      const _origStart = new Date(draggedAppointment.start_time);
      const _hourDiff = Math.abs(newStartTime - _origStart) / 3600000;
      if (_hourDiff >= 3) {
        const _hhmm = `${String(newStartTime.getHours()).padStart(2, '0')}:${String(newStartTime.getMinutes()).padStart(2, '0')}`;
        const _origHhmm = `${String(_origStart.getHours()).padStart(2, '0')}:${String(_origStart.getMinutes()).padStart(2, '0')}`;
        const _ok = window.confirm(
          `Grosser Zeitsprung: Termin wirklich von ${_origHhmm} auf ${_hhmm} Uhr verschieben?`
        );
        if (!_ok) {
          setDraggedAppointment(null);
          setDropTarget(null);
          return;
        }
      }

      const blockReason = getSlotBlockReason(
        trainerData.trainer.id,
        newStartTime,
        newEndTime,
        [draggedAppointment.id],
        {
          maxParticipants: getMaxParticipants(
            services,
            draggedAppointment.service_id
          ),
          occupancy: computeOccupancy(draggedAppointment),
        }
      );
      if (blockReason) {
        toast.error(blockReason);
        setDraggedAppointment(null);
        setDropTarget(null);
        return;
      }

      // Preflight: Kunden-Konflikt (Vortag/selber Tag/Folgetag)?
      const customerId =
        draggedAppointment.customer_id ||
        (draggedAppointment.customer_ids && draggedAppointment.customer_ids[0]);
      const proceed = await runCustomerConflictCheck(
        customerId,
        newStartTime.toISOString(),
        draggedAppointment.id,
        customers
      );
      if (!proceed) {
        setDraggedAppointment(null);
        setDropTarget(null);
        return;
      }

      await axios.put(`${API}/appointments/${draggedAppointment.id}`, {
        trainer_id: trainerData.trainer.id,
        start_time: newStartTime.toISOString(),
        end_time: newEndTime.toISOString(),
      });

      // 10/2026 Undo: zurück auf Original-Trainer/-Zeit (ID bleibt stabil).
      showUndoToast('Termin verschoben!', () =>
        axios.put(`${API}/appointments/${_undoId}`, _undoData)
      );
      setDraggedAppointment(null);
      setDropTarget(null);
      fetchAppointments();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Fehler beim Verschieben'
      );
      setDraggedAppointment(null);
      setDropTarget(null);
      // Iter 297d: Bei Fehler IMMER refetch.
      fetchAppointments();
    }
  };

  return {
    // State
    draggedAppointment,
    setDraggedAppointment,
    draggedParticipant,
    setDraggedParticipant,
    dropTarget,
    setDropTarget,
    dragCursorPos,
    // Handlers
    handleDragStart,
    handleParticipantDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    // Util re-exposed (used by handleMoveToSlot in page)
    getSlotBlockReason,
  };
};

export default useAppointmentDragDrop;
